/* Sage Massage: progressive enhancement; Shopify handles product and cart submissions. */
(() => {
  'use strict';
  const configNode = document.getElementById('ThemeConfig');
  const config = configNode ? JSON.parse(configNode.textContent) : {};
  const strings = config.strings || {};
  const root = (window.Shopify?.routes?.root || config.rootUrl || '/').replace(/\/?$/, '/');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const q = (selector, parent = document) => parent.querySelector(selector);
  const qa = (selector, parent = document) => Array.from(parent.querySelectorAll(selector));
  const ajaxHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const cartDialog = () => document.getElementById('CartDrawer');
  let cartBusy = false;
  let cartOpener = null;
  let cartRequest = null;
  function setCounts(count) { qa('[data-cart-count]').forEach(el => { el.textContent = String(count); }); }
  function openCart(opener) {
    const dialog = cartDialog();
    if (!dialog?.showModal) { window.location.assign(config.cartUrl || root + 'cart'); return; }
    cartOpener = opener || document.activeElement;
    if (!dialog.open) dialog.showModal();
    q('[data-cart-close]', dialog)?.focus();
  }
  function closeCart() { cartDialog()?.close(); cartOpener?.focus?.({ preventScroll: true }); }
  function renderCart(html, count) {
    if (typeof html !== 'string') return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.getElementById('CartDrawerContent');
    const current = document.getElementById('CartDrawerContent');
    if (!next || !current) return false;
    current.replaceWith(next);
    setCounts(count ?? Number(next.dataset.cartCountValue));
    return true;
  }
  async function refreshCart() {
    const dialog = cartDialog();
    if (!dialog) return false;
    if (cartRequest) return cartRequest;
    cartRequest = (async () => {
      const url = new URL(config.cartUrl || root + 'cart', window.location.origin);
      url.searchParams.set('section_id', dialog.dataset.sectionId);
      const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!response.ok) throw new Error(strings.error);
      return renderCart(await response.text());
    })();
    try { return await cartRequest; } finally { cartRequest = null; }
  }
  async function changeCart(key, quantity, trigger) {
    if (cartBusy || !Number.isSafeInteger(quantity) || quantity < 0) return;
    const dialog = cartDialog();
    if (!dialog) return;
    cartBusy = true;
    dialog.setAttribute('aria-busy', 'true');
    qa('button, input', dialog).forEach(el => { el.disabled = true; });
    const error = q('[data-cart-error]', dialog);
    if (error) error.hidden = true;
    try {
      const response = await fetch(root + 'cart/change.js', {
        method: 'POST', headers: ajaxHeaders,
        body: JSON.stringify({ id: key, quantity, sections: [dialog.dataset.sectionId], sections_url: config.cartUrl || root + 'cart' })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : strings.error);
      if (!renderCart(data.sections?.[dialog.dataset.sectionId], data.item_count)) {
        if (!await refreshCart()) { window.location.assign(config.cartUrl || root + 'cart'); return; }
      }
      const sameLine = qa('[data-cart-line]', dialog).find(el => el.dataset.cartLine === key);
      if (sameLine) (q('[data-cart-quantity]', sameLine) || q('[data-cart-close]', dialog))?.focus();
      else q('[data-cart-close]', dialog)?.focus();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
    } catch (err) {
      if (error) { error.textContent = err.message || strings.error; error.hidden = false; }
      if (trigger?.matches('input')) trigger.value = trigger.defaultValue;
    } finally {
      cartBusy = false; dialog.removeAttribute('aria-busy');
      qa('button, input', dialog).forEach(el => { el.disabled = false; });
    }
  }
  class SageProduct extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.form = q('form.product-form', this);
      this.select = q('[data-variant-select]', this);
      this.submitButton = q('[data-add-to-cart]', this);
      this.stickyButton = q('[data-sticky-submit]', this);
      this.track = q('[data-gallery-track]', this);
      this.slides = qa('[data-media-id]', this.track || this);
      this.thumbs = qa('[data-gallery-thumb]', this);
      this.slideIndex = 0;
      this.onVariantChange = () => this.updateVariant(true);
      this.select?.addEventListener('change', this.onVariantChange, { signal });
      q('[data-selling-plan]', this)?.addEventListener('change', () => this.updateVariant(false), { signal });
      this.form?.addEventListener('submit', event => this.addToCart(event), { signal });
      this.stickyButton?.addEventListener('click', () => {
        if (!this.form || this.submitButton?.disabled) return;
        if (!this.form.reportValidity()) {
          this.form.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'center' });
          return;
        }
        this.form.requestSubmit(this.submitButton);
      }, { signal });
      this.thumbs.forEach(thumb => thumb.addEventListener('click', event => {
        event.preventDefault(); this.goToMedia(thumb.dataset.galleryThumb);
      }, { signal }));
      q('[data-gallery-prev]', this)?.addEventListener('click', () => this.showSlide(this.slideIndex - 1), { signal });
      q('[data-gallery-next]', this)?.addEventListener('click', () => this.showSlide(this.slideIndex + 1), { signal });
      this.track?.addEventListener('scroll', () => {
        cancelAnimationFrame(this.galleryFrame);
        this.galleryFrame = requestAnimationFrame(() => {
          const width = this.track.clientWidth;
          this.slideIndex = Math.round(this.track.scrollLeft / Math.max(width, 1));
          this.markThumb();
        });
      }, { passive: true, signal });
      this.markThumb();
      this.updateVariant(false);
      if (this.select?.selectedOptions[0]?.dataset.media) this.goToMedia(this.select.selectedOptions[0].dataset.media);
      const sticky = q('[data-sticky-buy]', this);
      if (sticky && this.submitButton && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const entry = entries[0];
          const visible = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          sticky.hidden = !visible;
          sticky.classList.toggle('is-visible', visible);
        }, { threshold: 0 });
        this.observer.observe(this.submitButton);
      }
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null; this.observer?.disconnect(); cancelAnimationFrame(this.galleryFrame);
    }
    markThumb() {
      const activeId = this.slides[this.slideIndex]?.dataset.mediaId;
      this.thumbs.forEach(el => el.setAttribute('aria-current', String(el.dataset.galleryThumb === activeId)));
    }
    showSlide(index) {
      if (!this.track || !this.slides.length) return;
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({ left: this.track.clientWidth * index, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      this.slides.forEach((slide, i) => { if (i !== index) qa('video', slide).forEach(video => video.pause()); });
      this.slideIndex = index; this.markThumb();
    }
    goToMedia(id) { const index = this.slides.findIndex(el => el.dataset.mediaId === String(id)); if (index >= 0) this.showSlide(index); }
    updateVariant(updateUrl) {
      const option = this.select?.selectedOptions[0];
      if (!option) return;
      let d = { ...option.dataset };
      const plan = q('[data-selling-plan]', this);
      const allocationsNode = q('[data-selling-allocations]', this);
      if (plan && allocationsNode) {
        const allocations = JSON.parse(allocationsNode.textContent)[option.value] || [];
        Array.from(plan.options).forEach(opt => { opt.disabled = !!opt.value && !allocations.some(a => String(a.id) === opt.value); });
        if (plan.selectedOptions[0]?.disabled) plan.value = Array.from(plan.options).find(opt => !opt.disabled)?.value || '';
        const allocation = allocations.find(a => String(a.id) === plan.value);
        if (allocation) d = { ...d, ...allocation };
      }
      this.available = d.available === 'true' && (!plan?.required || !!plan.value);
      qa('[data-price]', this).forEach(el => { el.textContent = d.price; });
      qa('[data-sticky-price]', this).forEach(el => { el.textContent = d.price; });
      const onSale = Number(d.compareCents) > Number(d.priceCents);
      qa('[data-compare]', this).forEach(el => { el.textContent = d.compare; });
      qa('[data-compare-wrapper]', this).forEach(el => { el.hidden = !onSale; });
      qa('[data-sale]', this).forEach(el => {
        el.hidden = !onSale;
        el.textContent = onSale ? '-' + Math.round((Number(d.compareCents) - Number(d.priceCents)) / Number(d.compareCents) * 100) + '%' : '';
      });
      qa('[data-unit]', this).forEach(el => { el.hidden = !d.unit; el.textContent = d.unit || ''; });
      const quantity = q('input[name="quantity"]', this);
      if (quantity) {
        const min = Math.max(1, Number(d.min) || 1);
        const step = Math.max(1, Number(d.step) || 1);
        quantity.min = min; quantity.step = step;
        if (d.max) quantity.max = d.max; else quantity.removeAttribute('max');
        let value = Math.max(min, Number(quantity.value) || min);
        value = min + Math.ceil((value - min) / step) * step;
        if (d.max) value = Math.min(value, Number(d.max));
        quantity.value = value;
      }
      const label = this.available ? this.submitButton?.dataset.addLabel : strings.soldOut;
      if (this.submitButton) this.submitButton.disabled = !this.available || !!this.busy;
      if (this.stickyButton) this.stickyButton.disabled = !this.available || !!this.busy;
      qa('[data-add-text], [data-sticky-text]', this).forEach(el => { el.textContent = label || ''; });
      q('[data-dynamic-checkout]', this)?.classList.toggle('unavailable', !this.available);
      const inventory = q('[data-inventory-note]', this);
      if (inventory) {
        inventory.hidden = !this.available;
        const count = Number(d.inventory);
        const showCount = this.dataset.showStockCount === 'true' && count > 0 && count <= Number(this.dataset.inventoryThreshold);
        q('[data-inventory-text]', inventory).textContent = showCount ? this.dataset.stockCount.replace('[count]', count) : this.dataset.stockLabel;
      }
      if (updateUrl && d.media) this.goToMedia(d.media);
      if (updateUrl && this.dataset.productPage === 'true' && this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href); url.searchParams.set('variant', option.value); history.replaceState({}, '', url);
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variantId: option.value } }));
    }
    async addToCart(event) {
      if (typeof fetch !== 'function') return;
      event.preventDefault();
      if (this.busy || !this.available) return;
      if (!this.form.reportValidity()) return;
      this.busy = true;
      const error = q('[data-product-error]', this);
      if (error) error.hidden = true;
      this.submitButton.disabled = true;
      if (this.stickyButton) this.stickyButton.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      q('[data-add-text]', this).textContent = strings.adding;
      let added = false;
      try {
        const data = new FormData(this.form);
        const drawer = cartDialog();
        if (drawer) { data.append('sections', drawer.dataset.sectionId); data.append('sections_url', config.cartUrl || root + 'cart'); }
        const response = await fetch(config.cartAddUrl ? config.cartAddUrl + '.js' : root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : strings.error);
        added = true;
        if (config.cartType !== 'drawer' || !drawer) { window.location.assign(config.cartUrl || root + 'cart'); return; }
        const rendered = renderCart(result.sections?.[drawer.dataset.sectionId]);
        if (!rendered && !await refreshCart()) { window.location.assign(config.cartUrl || root + 'cart'); return; }
        openCart(this.submitButton);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { added: result } }));
      } catch (err) {
        if (added) { window.location.assign(config.cartUrl || root + 'cart'); return; }
        if (error) { error.textContent = err.message || strings.error; error.hidden = false; error.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'instant' : 'smooth' }); }
      } finally {
        this.busy = false; this.form.removeAttribute('aria-busy'); this.updateVariant(false);
      }
    }
  }
  if (!customElements.get('sage-product')) customElements.define('sage-product', SageProduct);
  document.addEventListener('click', async event => {
    const target = event.target;
    const open = target.closest('[data-cart-open]');
    if (open && config.cartType === 'drawer' && cartDialog()?.showModal) {
      event.preventDefault();
      try { if (!await refreshCart()) throw new Error(); openCart(open); } catch { window.location.assign(config.cartUrl || root + 'cart'); }
      return;
    }
    if (target.closest('[data-cart-close]')) { closeCart(); return; }
    if (target === cartDialog()) { closeCart(); return; }
    const remove = target.closest('[data-cart-remove]');
    if (remove?.closest('#CartDrawer')) { event.preventDefault(); await changeCart(remove.dataset.key, 0, remove); return; }
    const quantityButton = target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (quantityButton) {
      const control = quantityButton.closest('[data-quantity-control]');
      const input = q('input[type="number"]', control);
      if (!input || input.disabled) return;
      try { if (quantityButton.hasAttribute('data-quantity-plus')) input.stepUp(); else input.stepDown(); } catch { return; }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const scrollLink = target.closest('[data-scroll-product]');
    if (scrollLink) {
      const product = q('sage-product');
      if (product) { event.preventDefault(); (q('.product-form', product) || product).scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'center' }); }
    }
    if (target.closest('.menu-panel a')) target.closest('.mobile-menu')?.removeAttribute('open');
  });
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-cart-quantity]') && input.closest('#CartDrawer')) {
      if (!input.reportValidity()) return;
      changeCart(input.dataset.key, Number(input.value), input);
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') qa('.mobile-menu[open]').forEach(menu => { menu.removeAttribute('open'); q('summary', menu)?.focus(); });
  });
  document.addEventListener('shopify:block:select', event => {
    const details = event.target.matches('details') ? event.target : q('details', event.target);
    if (details) details.open = true;
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) thumb.closest('sage-product')?.goToMedia(thumb.dataset.galleryThumb);
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) { refreshCart().catch(() => {}); qa('sage-product').forEach(el => el.updateVariant(false)); }
  });
})();
